// frontend/src/modules/workspace/components/InviteMemberDialog.tsx
//
// Generic "invite member with roles" dialog. On success it surfaces the
// magic-link token in a copyable field (dev convenience), then the caller
// refreshes the member list.

import * as React from 'react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../../components/ui/dialog';
import { workspaceApi } from '../api';
import type { WorkspaceRole } from '../types';
import { CheckboxRow } from './CheckboxRow';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  roles: WorkspaceRole[];
  onInvited: () => void;
}

export function InviteMemberDialog({ open, onOpenChange, roles, onInvited }: Props) {
  const [email, setEmail] = React.useState('');
  const [name, setName] = React.useState('');
  const [roleKeys, setRoleKeys] = React.useState<Set<string>>(new Set());
  const [busy, setBusy] = React.useState(false);
  const [issuedToken, setIssuedToken] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open) {
      setEmail(''); setName(''); setRoleKeys(new Set()); setIssuedToken(null);
    }
  }, [open]);

  function toggleRole(key: string) {
    setRoleKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    if (!email.trim() || !name.trim()) {
      toast.error('Email and name are required');
      return;
    }
    setBusy(true);
    try {
      const r = await workspaceApi.inviteMember({
        email: email.trim(),
        name: name.trim(),
        role_keys: Array.from(roleKeys),
      });
      setIssuedToken(r.magic_link_token);
      toast.success('Invite sent', { description: `A magic link was emailed to ${email.trim()}.` });
      onInvited();
    } catch (err: any) {
      toast.error('Invite failed', { description: err.message ?? String(err) });
    } finally {
      setBusy(false);
    }
  }

  // Full, clickable invite URL — a bare token isn't usable without knowing
  // the /auth/set-password path, so we always share the complete link.
  const inviteUrl = issuedToken
    ? `${window.location.origin}/auth/set-password?token=${encodeURIComponent(issuedToken)}`
    : null;

  async function copyLink() {
    if (!inviteUrl) return;
    try {
      await navigator.clipboard.writeText(inviteUrl);
      toast.success('Invite link copied to clipboard');
    } catch {
      toast.error('Could not copy — copy manually');
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{issuedToken ? 'Invite sent' : 'Invite member'}</DialogTitle>
          {!issuedToken && (
            <DialogDescription>
              They'll receive a magic-link email to set a password and sign in. Pick the roles
              that decide what they can do.
            </DialogDescription>
          )}
        </DialogHeader>

        {!issuedToken ? (
          <form onSubmit={submit} className="grid gap-4 pt-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="grid gap-1.5">
                <Label>Email<span className="text-rose-500 ml-0.5">*</span></Label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="jane@company.com" required />
              </div>
              <div className="grid gap-1.5">
                <Label>Name<span className="text-rose-500 ml-0.5">*</span></Label>
                <Input value={name} onChange={(e) => setName(e.target.value)}
                  placeholder="Jane Smith" required />
              </div>
            </div>

            <div className="grid gap-1.5">
              <Label>Roles</Label>
              {roles.length === 0 ? (
                <p className="text-[12.5px] text-muted-foreground">No roles available.</p>
              ) : (
                <ul className="space-y-2 max-h-64 overflow-y-auto scrollbar-thin pr-0.5">
                  {roles.map((r) => (
                    <li key={r.id}>
                      <CheckboxRow
                        checked={roleKeys.has(r.key)}
                        onToggle={() => toggleRole(r.key)}
                        title={r.name}
                        subtitle={r.description ?? r.key}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button type="submit" disabled={busy || roleKeys.size === 0}>{busy ? 'Sending…' : 'Send invite'}</Button>
            </div>
          </form>
        ) : (
          <div className="grid gap-4 pt-2">
            <p className="text-[12.5px] text-muted-foreground leading-relaxed">
              We emailed a magic link to <span className="font-semibold text-foreground">{email}</span>.
              You can also copy the invite link below and share it directly.
            </p>
            <div className="grid gap-1.5">
              <Label className="text-xs">Invite link</Label>
              <Input readOnly value={inviteUrl ?? ''} onFocus={(e) => e.currentTarget.select()}
                className="font-mono text-xs" />
              <Button type="button" variant="outline" size="sm" onClick={copyLink} className="justify-self-start">
                Copy link
              </Button>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" onClick={() => onOpenChange(false)}>Done</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
